import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { last } from 'lodash';
import { type Feature, type MapBrowserEvent } from 'ol';
import { type Geometry } from 'ol/geom';
import { Draw, Modify, Select, Snap, Translate } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import type Map from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type MapGeometryItem } from '../createMapSchema.ts';
import { type IMapWidget, MapWidgetSelectionShape } from '../IMapWidget.ts';
import { DRAWING_LAYER_Z_INDEX } from '../MapWidgetLogic.ts';
import {
  convertGeometryItemsToFeatures,
  fitViewToFeatures,
} from './convertGeometryItemsToFeatures.ts';
import { createGeometryLabelStyle } from './createGeometryLabelStyle.ts';

/** Modalità di interazione sulla mappa. */
export type MapDrawingMode = 'view' | 'draw' | 'delete' | 'identify';

/**
 * Mappa lo shape di configurazione al tipo di geometria
 * OpenLayers usato dalla Draw interaction.
 */
function selectionShapeToDrawType(
  shape: MapWidgetSelectionShape,
): 'Point' | 'LineString' | 'Polygon' {
  switch (shape) {
    case MapWidgetSelectionShape.Point:
      return 'Point';
    case MapWidgetSelectionShape.Line:
      return 'LineString';
    case MapWidgetSelectionShape.Polygon:
      return 'Polygon';
  }
}

interface IMapDrawingInteractionsOptions {
  widget: IMapWidget;
  geometriesEditor: RelationEditor<MapGeometryItem>;
}

/**
 * Gestisce le interazioni di disegno sulla mappa, come il disegno
 * di geometrie e l'eliminazione di geometrie disegnate precedentemente.
 */
export function useMapDrawingInteractions(
  options: IMapDrawingInteractionsOptions,
) {
  const { widget, geometriesEditor } = options;

  /** Sorgente vettoriale condivisa per le geometrie disegnate. */
  const source = useRef(new VectorSource({ wrapX: false }));
  /** Layer vettoriale delle geometrie (serve per aggiornare lo stile). */
  const vectorLayerRef = useRef<VectorLayer<Feature<Geometry>> | null>(null);
  const interactions = useRef<{
    /**
     * Una Draw per ogni shape configurato, create una volta sola in
     * `initDrawing` e mantenute persistenti: al cambio shape ne attiviamo
     * una senza ricrearle, così lo Snap resta sempre l'ultima interaction
     * registrata (ordine richiesto da OpenLayers).
     */
    draws: Partial<Record<MapWidgetSelectionShape, Draw>>;
    snap?: Snap;
    modify?: Modify;
    translate?: Translate;
    remove?: Select;
  }>({ draws: {} });

  /** Tipologie di geometria abilitate nella configurazione del widget. */
  const shapes = widget.selection?.shapes ?? [MapWidgetSelectionShape.Polygon];

  /**
   * Tipologia di disegno attualmente selezionata dall'utente.
   * Determina il tipo di Draw interaction attiva sulla mappa.
   */
  const [activeShape, setActiveShape] = useState<MapWidgetSelectionShape>(
    shapes[0],
  );
  const activeShapeRef = useRef(activeShape);
  activeShapeRef.current = activeShape;

  const [mode, setMode] = useState('view' as MapDrawingMode);
  const modeRef = useRef(mode);

  /**
   * Aggiorna la modalità di disegno in base alla selezione dell'utente.
   */
  const handleModeChange = useCallback((nextMode: MapDrawingMode) => {
    setMode(nextMode);
    modeRef.current = nextMode; // Necessario per le closure di OpenLayers

    const { draws, snap, modify, translate, remove } = interactions.current;
    for (const draw of Object.values(draws)) {
      draw?.setActive(false);
    }
    snap?.setActive(false);
    modify?.setActive(false);
    translate?.setActive(false);
    remove?.setActive(false);

    switch (nextMode) {
      case 'view':
        break;
      case 'draw':
        // Attiva solo la Draw dello shape correntemente selezionato.
        draws[activeShapeRef.current]?.setActive(true);
        snap?.setActive(true);
        modify?.setActive(true);
        translate?.setActive(true);
        break;
      case 'delete':
        remove?.setActive(true);
        break;
      case 'identify':
        break;
    }
  }, []);

  /**
   * Inizializza il layer di disegno e le interaction OL
   * (Draw, Modify, Translate, Snap, Select/Remove).
   * Viene invocato una volta sola in `initMap` del componente.
   */
  const initDrawing = useCallback(
    (map: Map | null) => {
      if (!map) return;

      const vectorLayer = new VectorLayer({
        source: source.current,
        style: createGeometryLabelStyle(widget),
      });
      vectorLayerRef.current = vectorLayer;
      map.addLayer(vectorLayer);
      vectorLayer.setZIndex(DRAWING_LAYER_Z_INDEX);

      const initialFeatures = convertGeometryItemsToFeatures(
        geometriesEditor.items,
      );

      if (initialFeatures.length > 0) {
        source.current.addFeatures(initialFeatures);
        fitViewToFeatures(map, source.current);
      }

      // Spostamento intera geometria. Va aggiunta PRIMA di Modify: OpenLayers
      // processa le interaction in ordine inverso di inserimento, quindi Modify
      // (aggiunta dopo) intercetta per prima i click sui vertici. Senza questo
      // ordine Translate catturerebbe il click sull'intera geometria e
      // impedirebbe la modifica dei vertici.
      const translate = new Translate({ layers: [vectorLayer] });
      map.addInteraction(translate);
      interactions.current.translate = translate;

      // Modifica dei vertici
      const modify = new Modify({ source: source.current });
      map.addInteraction(modify);
      interactions.current.modify = modify;

      // Una Draw per ogni shape configurato, tutte disattivate. Vanno
      // aggiunte PRIMA dello Snap, così lo Snap resta l'ultima interaction
      // registrata (ordine richiesto da OpenLayers per lo snapping).
      const drawShapes = widget.selection?.shapes ?? [
        MapWidgetSelectionShape.Polygon,
      ];
      for (const shape of drawShapes) {
        const draw = new Draw({
          source: source.current,
          type: selectionShapeToDrawType(shape),
        });
        draw.setActive(false);
        map.addInteraction(draw);
        interactions.current.draws[shape] = draw;
      }

      const snap = new Snap({ source: source.current });
      map.addInteraction(snap);
      interactions.current.snap = snap;

      const remove = new Select({
        layers: [vectorLayer],
        style: new Style({
          fill: new Fill({
            color: '#eeeeee',
          }),
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 0.7)',
            width: 2,
          }),
        }),
      });
      map.addInteraction(remove);
      interactions.current.remove = remove;

      const hoveredFeatures = new Set<Feature<Geometry>>();

      // Stile di evidenziazione in modalità elimina. Deve coprire tutti i tipi
      // di geometria: `image` (cerchio) per i punti — senza il quale un Point
      // non verrebbe disegnato affatto —, `stroke` per linee e contorni,
      // `fill` per il riempimento dei poligoni.
      const deleteHoverStyle = new Style({
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: 'rgba(255, 0, 0, 0.6)' }),
          stroke: new Stroke({ color: 'red', width: 2 }),
        }),
        stroke: new Stroke({ color: 'red', width: 3 }),
        fill: new Fill({ color: 'rgba(255, 0, 0, 0.4)' }),
      });

      map.on('pointermove', function (this: Map, e: MapBrowserEvent<any>) {
        if (modeRef.current !== 'delete') return;

        // Reset style
        if (hoveredFeatures.size > 0) {
          hoveredFeatures.forEach(f => {
            f.setStyle();
          });
          hoveredFeatures.clear();
        }

        // Rilevamento per pixel con tolleranza: linee e punti hanno un'area di
        // contatto minima e `getFeaturesAtCoordinate` (intersezione esatta con
        // la geometria) non li intercetta all'hover. `getFeaturesAtPixel` usa
        // lo stesso hit-test del Select, così l'evidenziazione è coerente col
        // click di eliminazione. `last` prende la feature più in alto.
        const hit = map.getFeaturesAtPixel(e.pixel, {
          layerFilter: layer => layer === vectorLayer,
          hitTolerance: 6,
        });
        const hitTarget = last(hit) as Feature<Geometry> | undefined;
        if (hitTarget != null) {
          hitTarget.setStyle(deleteHoverStyle);
          hoveredFeatures.add(hitTarget);
          this.getTargetElement().style.cursor = 'pointer';
        } else {
          this.getTargetElement().style.cursor = '';
        }
      });

      handleModeChange('view');
    },
    [geometriesEditor.items, handleModeChange, widget],
  );

  /** Aggiorna lo style del VectorLayer quando cambia la configurazione delle etichette. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo labeling influenza lo style
  useEffect(() => {
    vectorLayerRef.current?.setStyle(createGeometryLabelStyle(widget));
  }, [widget.labeling]);

  // Retrocompatibilità: useMapGeometryCounter assegna geometryIndex nel form state
  // alle geometrie pre-migrazione, ma le Feature OL già caricate nel source non
  // vengono aggiornate. Questo effect sincronizza il geometryIndex dagli item
  // del form alle Feature OL così che le etichette siano visibili subito.
  useEffect(() => {
    for (const item of geometriesEditor.items) {
      if (item.geometryIndex == null) continue;
      const feature = source.current.getFeatureById(item.id);
      if (!feature) continue;
      if (feature.get('geometryIndex') !== item.geometryIndex) {
        feature.set('geometryIndex', item.geometryIndex);
      }
    }
  }, [geometriesEditor.items]);

  /**
   * Aggiorna lo shape attivo attivando la Draw corrispondente e
   * disattivando le altre. Le Draw sono già registrate (una per shape),
   * quindi non tocchiamo l'ordine delle interaction: lo Snap resta
   * l'ultima. La Draw è attiva solo se siamo in modalità disegno.
   */
  const updateActiveShape = useCallback((shape: MapWidgetSelectionShape) => {
    setActiveShape(shape);
    activeShapeRef.current = shape;

    const isDrawing = modeRef.current === 'draw';
    for (const [shapeKey, draw] of Object.entries(interactions.current.draws)) {
      const shouldBeActive = shapeKey === shape && isDrawing;
      // Interrompe un eventuale disegno in corso sulla Draw che disattiviamo.
      if (draw?.getActive() && !shouldBeActive) {
        draw.abortDrawing();
      }
      draw?.setActive(shouldBeActive);
    }
  }, []);

  return {
    interactions,
    featuresSource: source,
    mode,
    activeShape,
    updateActiveShape,
    initDrawing,
    handleModeChange,
  };
}
